import { formatDate } from 'lib/helpers/dateUtils';
import { formatLargeNumber, mString } from 'lib/helpers/utility';

import { CHART_INK } from './constants';

export const currencyAxisLabel = ({ value }) => mString(value, false).replace(/\.00$/, '');

export const numberAxisLabel = ({ value }) => formatLargeNumber(value);

const tooltipRenderer = (
  formatValue,
  { timezone, dateFormat = 'MMM d, yyyy', formatLabel } = {}
) => {
  return ({ datum, xKey, yKey, yName }) => {
    const label = formatLabel
      ? formatLabel(datum[xKey])
      : formatDate(datum[xKey], timezone, dateFormat);
    const value = formatValue(datum[yKey]);

    return yName
      ? { title: yName, content: `${label}: ${value}` }
      : { title: label, content: value };
  };
};

export const currencyTooltip = ({ cents = false, ...rest } = {}) =>
  tooltipRenderer((value) => mString(value, cents), rest);

export const countTooltip = (params = {}) =>
  tooltipRenderer((value) => formatLargeNumber(Math.round(value ?? 0)), params);

const bareAxis = (type, position) => ({
  type,
  position,
  line: { enabled: false },
  tick: { enabled: false },
  gridLine: { enabled: false },
  crosshair: { enabled: false }
});

const blankAxis = (type, position) => ({
  ...bareAxis(type, position),
  label: { enabled: false }
});

const datedAxis = (type, position) => ({
  ...bareAxis(type, position),
  label: { fontSize: 10, avoidCollisions: true, rotation: 0 }
});

const sparklineSeries = {
  area: (color) => ({
    type: 'area',
    fill: color,
    stroke: color,
    strokeWidth: 2,
    shadow: { enabled: false }
  }),
  bar: (color) => ({
    type: 'bar',
    fill: color,
    strokeWidth: 0,
    cornerRadius: 1,
    shadow: { enabled: false }
  })
};

export const sparklineOptions = ({
  data,
  xKey,
  yKey,
  yName,
  color,
  type = 'area',
  xAxisType = 'category',
  xAxisLabels = false,
  tooltip
}) => ({
  data,
  padding: { top: 6, right: 4, bottom: xAxisLabels ? 0 : 2, left: 4 },
  legend: { enabled: false },
  series: [
    {
      ...(sparklineSeries[type] || sparklineSeries.area)(color),
      xKey,
      yKey,
      ...(yName && { yName }),
      ...(tooltip && { tooltip })
    }
  ],
  axes: [
    xAxisLabels ? datedAxis(xAxisType, 'bottom') : blankAxis(xAxisType, 'bottom'),
    blankAxis('number', 'left')
  ]
});

// Numbers are comma-grouped here so no caller has to remember; strings (already-formatted currency
// from mString, for instance) pass through untouched.
export const donutInnerLabels = ({ value, caption }) => [
  {
    text: typeof value === 'number' ? formatLargeNumber(value) : `${value ?? ''}`,
    color: CHART_INK.value,
    fontSize: 20,
    fontWeight: 600
  },
  { text: caption, color: CHART_INK.caption, fontSize: 12, margin: 4 }
];
